<template>
  <div>
    <div class="info-block px-3 py-1 mb-6 d-flex align-center">
      <v-icon size="24" icon="$warning" />
      <span class="subtitle2 ml-2" v-html="$t('modals.exportTelemetry.disclaimer')" />
    </div>
    <v-radio-group v-model="localValue.type" hide-details>
      <v-radio
        v-for="choice in [ExportChoice.ALL, ExportChoice.SPECIFIC]"
        :key="choice"
        class="mb-3"
        :value="choice"
        :ripple="false"
        :label="$t(`modals.exportTelemetry.choices.${choice}`)"
        :data-testid="`export-telemetry-choice-${choice}`"
      ></v-radio>
    </v-radio-group>
    <cui-filter-wrapper :render-clear-button="false" class="mt-2">
      <div class="d-flex flex-column">
        <v-select
          v-if="localValue.type === ExportChoice.SPECIFIC"
          v-model="localValue.filters.types"
          :items="telemetryTypes"
          bg-color="white"
          menu-icon="icon-arrow-down-xs"
          :menu-props="{ maxHeight: '300' }"
          class="filter-menu filter mb-2"
          :class="{ 'filter-active': localValue.filters.types?.length }"
          :placeholder="$t('modals.exportTelemetry.typesPlaceholder')"
          density="compact"
          variant="outlined"
          multiple
          rounded
          hide-details
          background-color="white"
          data-testid="export-telemetry-types-filter"
        >
          <template #selection="{ index }">
            <span v-if="index === 0" class="body2">
              {{ $t("modals.exportTelemetry.typesPlaceholder") }}
            </span>
          </template>
          <template #label>
            <span v-if="localValue.filters.types?.length" class="filter-label">
              {{ localValue.filters.types.length }}
            </span>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <template v-slot:prepend="{ isActive }">
                <v-list-item-action start>
                  <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
                </v-list-item-action>
              </template>

              <v-list-item-title title="">
                {{ $t(`edr.telemetryTab.types.${item.raw}`) }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-select>
        <date-range-picker
          v-model="localValue.filters.timeRange"
          :min-date="minDate"
          :max-date="maxDate"
          :range="dateRangeConfig"
          :preset-dates="presets"
          :placeholder="$t('reports.exports.filters.dateRange')"
          class="mr-2 filter"
          :teleport="true"
          hide-menu-arrow
          @click.stop.prevent
        ></date-range-picker>
      </div>
    </cui-filter-wrapper>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { type EdrTelemetryExportFilters, type ExportDialogPayload } from "@/_store/exports.module";
import DateRangePicker from "@/components/DateRangePicker.vue";
import { getCurrentDate, getNDaysAgo, getYesterday } from "@/constants/date-range-picker";
import { CuiFilterWrapper } from "@coronet/cui";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useI18n } from "vue-i18n";
import { ExportChoice } from "@/constants/exports.ts";
import { TelemetryType } from "@/constants/edr.ts";

// Telemetry exports are limited to a 14-day window. maxRange is the maximum
// day-difference between range bounds, so 13 yields a 14-day inclusive span.
const TELEMETRY_EXPORT_MAX_RANGE_DAYS = 13;
const TELEMETRY_RETENTION_DAYS = 90;

export default defineComponent({
  components: { CuiFilterWrapper, DateRangePicker },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const i18n = useI18n();

    const telemetryTypes = [
      TelemetryType.ACCOUNT_EVENT,
      TelemetryType.REGISTRY_KEY,
      TelemetryType.SCHEDULED_TASK,
      TelemetryType.USB_DEVICE_ACTIVITY,
    ];

    const minDate = computed(() => getNDaysAgo(TELEMETRY_RETENTION_DAYS));
    const maxDate = computed(() => getCurrentDate());
    const dateRangeConfig = {
      showLastInRange: false,
      maxRange: TELEMETRY_EXPORT_MAX_RANGE_DAYS,
    };

    const presets = computed(() => [
      {
        label: i18n.t("dateRangePicker.today"),
        value: [getCurrentDate(), getCurrentDate()],
      },
      {
        label: i18n.t("dateRangePicker.yesterday"),
        value: [getYesterday(), getYesterday()],
      },
      {
        label: i18n.t("dateRangePicker.lastSevenDays"),
        value: [getNDaysAgo(6), getCurrentDate()],
      },
      {
        label: i18n.t("dateRangePicker.lastFourteenDays"),
        value: [getNDaysAgo(13), getCurrentDate()],
      },
    ]);

    const localValue = ref<ExportDialogPayload<EdrTelemetryExportFilters>>({
      type: ExportChoice.ALL,
      filters: {
        types: [],
        timeRange: { start: "", end: "" },
      },
    });

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    return {
      localValue,
      ExportChoice,
      telemetryTypes,
      minDate,
      maxDate,
      dateRangeConfig,
      presets,
    };
  },
});
</script>

<style lang="scss" scoped>
.filter {
  width: 240px !important;
}

:deep(*) {
  .clear-btn {
    display: none !important;
  }
}
</style>
